/**
 * WAF custom Firewall Rules (Owl/Mlytics). Service-scoped (path: `/services/:service_id/`) and
 * domain-anchored (`?domain=`). Rules are an ordered list (by priority) of
 * match `conditions` + an `action`.
 *
 * Backed by `modules/service/waf/controller_firewall.go`:
 *   - GET    /services/:service_id/waf/firewall/rules           → a domain's rules (sorted by priority)
 *   - POST   /services/:service_id/waf/firewall/rules           → create a rule
 *   - PATCH  /services/:service_id/waf/firewall/rules/:id        → replace a rule (full body)
 *   - PATCH  /services/:service_id/waf/firewall/rules/:id/enable → toggle a rule on/off
 *   - PUT    /services/:service_id/waf/firewall/rules            → reorder (priority from position)
 *   - DELETE /services/:service_id/waf/firewall/rules/:id        → delete a rule
 *
 * List returns a plain array under `data` (not paginated).
 */
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationOptions,
} from '@tanstack/react-query';
import { serviceApiV3, wafKeys, unwrap } from './_shared';

/* ----------------------------- Rule-builder schema ----------------------------- */

/** One selectable value for an action field (vendor mixes string/number values). */
export interface FirewallFieldOption {
  label?: string;
  value: string;
}

/** One comparison a field supports. `options` carries allowed values (methods, geo). */
export interface FirewallOperator {
  value: string;       // streq | pm | rx | geo_pm | within ...
  label: string;
  type: string;        // string | number | geo
  example?: string;
  options?: string[];
  input_value: boolean; // needs a value
}

/** One matchable request attribute. `input_value` = the field itself takes a key. */
export interface FirewallField {
  value: string;       // e.g. "ARGS:match"
  label: string;
  type: string;
  example?: string;
  description?: string;
  input_value: boolean;
  operators: FirewallOperator[];
}

/** One configurable input of an action. */
export interface FirewallActionField {
  field: string;       // totalCount | blockTime | mode | ttl | code | url ...
  label: string;
  type: string;        // number | string
  unit?: string;
  example?: string;
  range?: number[];
  mode?: string;
  options?: FirewallFieldOption[];
}

/** One rule action (vendor `value`: block|pass|rateLimit|blockRedirect|challenge). */
export interface FirewallActionSchema {
  value: string;
  label: string;
  type: string;
  input_value: boolean;
  additional_fields?: FirewallActionField[];
}

/** Rule-builder catalog: matchable fields + available actions. */
export interface FirewallRuleSchema {
  fields: FirewallField[];
  actions: FirewallActionSchema[];
}

/** Fields/operators/actions vocabulary for building custom firewall rules. */
export function useWafFirewallSchema(serviceId: string | undefined) {
  return useQuery<FirewallRuleSchema>({
    queryKey: wafKeys.firewallSchema(serviceId ?? ''),
    enabled: !!serviceId,
    staleTime: 30 * 60_000,
    queryFn: async () =>
      unwrap<FirewallRuleSchema>(
        await serviceApiV3.get(`/services/${serviceId}/waf/firewall/schema`),
      ) ?? { fields: [], actions: [] },
  });
}

/** Rule action — `type` is block|pass|rate_limit|block_redirect|challenge; the rest is type-specific. */
export interface FirewallRuleAction {
  type: string;
  code?: number;        // block_redirect: 301|302|303|307|308
  url?: string;         // block_redirect target
  mode?: string;        // challenge: javascript|javascript-nodelay|human
  ttl?: number;         // challenge passage seconds
  total_count?: number; // rate_limit: queries per minute
  block_time?: number;  // rate_limit (block mode): block seconds [10,86400]
  // rate_limit response mode: "block" (default) or "challenge". When "challenge",
  // block_time is omitted and the challenge is set by the two fields below.
  rate_limit_mode?: string;            // block | challenge
  rate_limit_challenge_mode?: string;  // javascript|javascript-nodelay|human
  rate_limit_challenge_ttl?: number;   // challenge passage seconds
}

/** One match condition. `logic` ("and"|"or") connects to the NEXT condition. */
export interface FirewallRuleCondition {
  field: string;
  key?: string;
  operator: string;
  value?: string;
  values?: string[];
  logic?: string;
}

/** A stored custom firewall rule (`GET /waf/firewall/rules`, sorted by priority). */
export interface FirewallRule {
  id: string;
  rule_id: number;
  name: string;
  enable: boolean;
  priority: number;
  action: FirewallRuleAction;
  conditions: FirewallRuleCondition[];
  created_at?: number;
  updated_at?: number;
}

/** Create/update body (full replace on update). 1–5 conditions. */
export interface FirewallRuleInput {
  name: string;
  enable?: boolean;
  action: FirewallRuleAction;
  conditions: FirewallRuleCondition[];
}

/** A domain's custom firewall rules (sorted by priority). */
export function useWafFirewallRules(serviceId: string | undefined, domain: string | undefined) {
  return useQuery<FirewallRule[]>({
    queryKey: wafKeys.firewallRules(serviceId ?? '', domain ?? ''),
    enabled: !!serviceId && !!domain,
    staleTime: 15_000,
    queryFn: async () =>
      unwrap<FirewallRule[]>(
        await serviceApiV3.get(`/services/${serviceId}/waf/firewall/rules`, { params: { domain } }),
      ) ?? [],
  });
}

function invalidateRules(qc: ReturnType<typeof useQueryClient>, serviceId?: string) {
  if (serviceId) qc.invalidateQueries({ queryKey: ['waf', serviceId, 'firewall-rules'] });
}

/** Create a firewall rule (`POST /waf/firewall/rules`). */
export function useCreateFirewallRule(
  serviceId: string | undefined,
  options?: UseMutationOptions<unknown, Error, { domain: string; input: FirewallRuleInput }>,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ domain, input }: { domain: string; input: FirewallRuleInput }) =>
      unwrap(
        await serviceApiV3.post(`/services/${serviceId}/waf/firewall/rules`, input, { params: { domain } }),
      ),
    ...options,
    onSuccess: (...args) => { invalidateRules(qc, serviceId); options?.onSuccess?.(...args); },
  });
}

/** Replace a firewall rule (`PATCH /waf/firewall/rules/:id`, full body). */
export function useUpdateFirewallRule(
  serviceId: string | undefined,
  options?: UseMutationOptions<unknown, Error, { domain: string; id: string; input: FirewallRuleInput }>,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ domain, id, input }: { domain: string; id: string; input: FirewallRuleInput }) =>
      unwrap(
        await serviceApiV3.patch(`/services/${serviceId}/waf/firewall/rules/${id}`, input, { params: { domain } }),
      ),
    ...options,
    onSuccess: (...args) => { invalidateRules(qc, serviceId); options?.onSuccess?.(...args); },
  });
}

/** Toggle a firewall rule on/off (`PATCH /waf/firewall/rules/:id/enable`). */
export function useSetFirewallRuleEnabled(
  serviceId: string | undefined,
  options?: UseMutationOptions<unknown, Error, { domain: string; id: string; enable: boolean }>,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ domain, id, enable }: { domain: string; id: string; enable: boolean }) =>
      unwrap(
        await serviceApiV3.patch(`/services/${serviceId}/waf/firewall/rules/${id}/enable`, { enable }, { params: { domain } }),
      ),
    ...options,
    onSuccess: (...args) => { invalidateRules(qc, serviceId); options?.onSuccess?.(...args); },
  });
}

/** Delete a firewall rule (`DELETE /waf/firewall/rules/:id`). */
export function useDeleteFirewallRule(
  serviceId: string | undefined,
  options?: UseMutationOptions<void, Error, { domain: string; id: string }>,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ domain, id }: { domain: string; id: string }) => {
      await serviceApiV3.delete(`/services/${serviceId}/waf/firewall/rules/${id}`, { params: { domain } });
    },
    ...options,
    onSuccess: (...args) => { invalidateRules(qc, serviceId); options?.onSuccess?.(...args); },
  });
}
